import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Ticket, Box, Search, ArrowLeft, ExternalLink, ShieldCheck, AlertTriangle, BookOpen } from 'lucide-react'
import {
  usePortalMeQuery,
  usePortalTicketsQuery,
  usePortalTicketQuery,
  usePortalAssetsQuery,
  usePortalAssetQuery,
  usePortalKbSearchQuery,
} from './customer-portal-api'
import { PaginationBar, EmptyState } from '@/components/shared/list-states'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/misc'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { useDebounced } from '@/lib/use-debounced'
import { formatDate, formatDateTime } from '@/lib/format'

// ─── OVERVIEW ──────────────────────────────────────────────
export function PortalOverviewPage() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading } = usePortalMeQuery(token!)

  if (isLoading || !data) {
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
    )
  }

  const { dashboard } = data.data

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard
          label="Open tickets"
          value={dashboard.open_tickets_count}
          icon={<Ticket className="h-4 w-4 text-amber-600" />}
        />
        <StatCard
          label="Resolved (30 days)"
          value={dashboard.recent_resolved_count}
          icon={<ShieldCheck className="h-4 w-4 text-emerald-600" />}
        />
        <StatCard
          label="Assets on file"
          value={dashboard.asset_count}
          icon={<Box className="h-4 w-4 text-accent" />}
        />
      </div>

      {dashboard.most_recent_ticket && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Most recent ticket</CardTitle>
          </CardHeader>
          <CardContent>
            <Link
              to={`/portal/${token}/tickets/${dashboard.most_recent_ticket.id}`}
              className="flex items-center justify-between rounded-md border border-border p-3 transition-colors hover:bg-muted/50"
            >
              <div>
                <p className="font-medium">{dashboard.most_recent_ticket.subject}</p>
                <p className="text-xs text-muted-foreground">{formatDateTime(dashboard.most_recent_ticket.created_at)}</p>
              </div>
              <Badge variant="muted">{dashboard.most_recent_ticket.status}</Badge>
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <QuickLink to={`/portal/${token}/tickets`} icon={Ticket} title="View all tickets" />
        <QuickLink to={`/portal/${token}/assets`} icon={Box} title="View my assets" />
      </div>
    </div>
  )
}

function StatCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
          {icon}
        </div>
        <p className="mt-2 font-display text-3xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  )
}

function QuickLink({ to, icon: Icon, title }: { to: string; icon: React.ElementType; title: string }) {
  return (
    <Link to={to} className="block">
      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5 text-accent" />
            <p className="font-medium">{title}</p>
          </div>
          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
        </CardContent>
      </Card>
    </Link>
  )
}

// ─── TICKETS LIST ──────────────────────────────────────────
export function PortalTicketsPage() {
  const { token } = useParams<{ token: string }>()
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('all')

  const { data, isLoading } = usePortalTicketsQuery({
    token: token!, page, per_page: 20,
    status: status === 'all' ? undefined : status,
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">My Tickets</h2>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1) }}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card>
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : !data || data.data.length === 0 ? (
          <EmptyState title="No tickets" description="Service tickets associated with your account will appear here." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Asset</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((t) => (
                  <TableRow key={t.id} className="cursor-pointer">
                    <TableCell>
                      <Link to={`/portal/${token}/tickets/${t.id}`} className="font-medium hover:text-accent">
                        {t.subject}
                      </Link>
                      {t.ticket_number && <p className="font-mono text-[10px] text-muted-foreground">{t.ticket_number}</p>}
                    </TableCell>
                    <TableCell>
                      {t.priority && <Badge variant={t.priority.severity_level >= 4 ? 'destructive' : t.priority.severity_level >= 3 ? 'warning' : 'muted'}>{t.priority.name}</Badge>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{t.asset?.name ?? '—'}</TableCell>
                    <TableCell>
                      <Badge variant={t.status === 'resolved' || t.status === 'closed' ? 'success' : t.status === 'in_progress' ? 'accent' : 'warning'}>
                        {t.status_label ?? t.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDateTime(t.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationBar meta={data.meta} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  )
}

// ─── TICKET DETAIL ─────────────────────────────────────────
export function PortalTicketDetailPage() {
  const { token, ticketId } = useParams<{ token: string; ticketId: string }>()
  const { data, isLoading, isError } = usePortalTicketQuery({ token: token!, ticketId: ticketId! })

  if (isLoading) return <Skeleton className="h-64" />
  if (isError || !data) return <EmptyState title="Ticket not found" description="This ticket may have been deleted or is no longer accessible." />

  const t = data.data

  return (
    <div className="space-y-4">
      <Link to={`/portal/${token}/tickets`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to tickets
      </Link>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="text-xl">{t.subject}</CardTitle>
              {t.ticket_number && <p className="mt-1 font-mono text-xs text-muted-foreground">{t.ticket_number}</p>}
            </div>
            <Badge variant={t.status === 'resolved' || t.status === 'closed' ? 'success' : t.status === 'in_progress' ? 'accent' : 'warning'}>
              {t.status_label ?? t.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            {t.priority && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Priority</p>
                <Badge variant={t.priority.severity_level >= 4 ? 'destructive' : t.priority.severity_level >= 3 ? 'warning' : 'muted'} className="mt-1">{t.priority.name}</Badge>
              </div>
            )}
            {t.category && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Category</p>
                <Badge variant="muted" className="mt-1">{t.category.name}</Badge>
              </div>
            )}
            {t.asset && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Asset</p>
                <Link to={`/portal/${token}/assets/${t.asset.id}`} className="mt-1 inline-flex items-center gap-1 text-sm hover:text-accent">
                  {t.asset.name}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>

          {t.description && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Description</p>
              <p className="mt-1 whitespace-pre-wrap text-sm">{t.description}</p>
            </div>
          )}

          {t.resolution_notes && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <p className="font-mono text-[10px] font-medium uppercase tracking-wider text-emerald-900">Resolution</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-emerald-900">{t.resolution_notes}</p>
            </div>
          )}

          <div className="grid gap-3 border-t border-border pt-3 sm:grid-cols-2 text-xs text-muted-foreground">
            <div><span className="font-medium">Created:</span> {formatDateTime(t.created_at)}</div>
            {t.resolved_at && <div><span className="font-medium">Resolved:</span> {formatDateTime(t.resolved_at)}</div>}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── ASSETS LIST ───────────────────────────────────────────
export function PortalAssetsPage() {
  const { token } = useParams<{ token: string }>()
  const [page, setPage] = useState(1)

  const { data, isLoading } = usePortalAssetsQuery({ token: token!, page, per_page: 20 })

  return (
    <div className="space-y-4">
      <h2 className="font-display text-lg font-semibold">My Assets</h2>
      <Card>
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : !data || data.data.length === 0 ? (
          <EmptyState title="No assets" description="Assets registered to your account will appear here." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Warranty</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <Link to={`/portal/${token}/assets/${a.id}`} className="font-medium hover:text-accent">
                        {a.name}
                      </Link>
                      <p className="font-mono text-[10px] text-muted-foreground">{a.asset_code}</p>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.category ?? '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.location_text ?? '—'}</TableCell>
                    <TableCell>
                      {a.is_under_warranty ? <Badge variant="success">Active</Badge> :
                        a.warranty_expires_at ? <Badge variant="muted">Expired</Badge> :
                          <Badge variant="muted">—</Badge>}
                    </TableCell>
                    <TableCell><Badge variant="muted">{a.status_label ?? a.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationBar meta={data.meta} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  )
}

// ─── ASSET DETAIL ──────────────────────────────────────────
export function PortalAssetDetailPage() {
  const { token, assetId } = useParams<{ token: string; assetId: string }>()
  const { data, isLoading, isError } = usePortalAssetQuery({ token: token!, assetId: assetId! })

  if (isLoading) return <Skeleton className="h-64" />
  if (isError || !data) return <EmptyState title="Asset not found" description="This asset is no longer accessible." />

  const a = data.data

  return (
    <div className="space-y-4">
      <Link to={`/portal/${token}/assets`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to assets
      </Link>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-xl">{a.name}</CardTitle>
              <p className="mt-1 font-mono text-xs text-muted-foreground">{a.asset_code}</p>
            </div>
            <Badge variant="muted">{a.status_label ?? a.status}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {a.description && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Description</p>
              <p className="mt-1 text-sm">{a.description}</p>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <DetailItem label="Category" value={a.category} />
            <DetailItem label="Location" value={a.location_text} />
            <DetailItem label="Manufacturer" value={a.manufacturer} />
            <DetailItem label="Model" value={a.model_number} />
            <DetailItem label="Serial" value={a.serial_number} mono />
            <DetailItem label="Installed" value={a.installed_at ? formatDate(a.installed_at) : null} />
          </div>
          {a.warranty_expires_at && (
            <div className={`rounded-md border p-3 ${a.is_under_warranty ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
              <div className="flex items-center gap-2 text-sm">
                {a.is_under_warranty
                  ? <ShieldCheck className="h-4 w-4 text-emerald-700" />
                  : <AlertTriangle className="h-4 w-4 text-amber-700" />}
                <p className={a.is_under_warranty ? 'text-emerald-900' : 'text-amber-900'}>
                  Warranty {a.is_under_warranty ? 'active until' : 'expired on'} {formatDate(a.warranty_expires_at)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function DetailItem({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-sm ${mono ? 'font-mono text-xs' : ''}`}>{value ?? '—'}</p>
    </div>
  )
}

// ─── KB SEARCH ─────────────────────────────────────────────
export function PortalKbPage() {
  const { token } = useParams<{ token: string }>()
  const [query, setQuery] = useState('')
  const debounced = useDebounced(query, 300)

  const { data, isLoading } = usePortalKbSearchQuery(
    { token: token!, q: debounced },
    { skip: !debounced || debounced.length < 2 }
  )

  return (
    <div className="space-y-4">
      <h2 className="font-display text-lg font-semibold">Knowledge Base</h2>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles, FAQs, troubleshooting guides…"
              className="pl-9"
              autoFocus
            />
          </div>
        </CardContent>
      </Card>

      {!debounced || debounced.length < 2 ? (
        <EmptyState
          title="Start typing"
          description="Search the knowledge base by typing keywords related to your question."
        />
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          title="No results"
          description={`No articles match "${debounced}". Try different keywords.`}
        />
      ) : (
        <div className="space-y-2">
          {data.data.map((r) => (
            <Card key={r.id}>
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <BookOpen className="mt-0.5 h-4 w-4 text-accent" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{r.title}</h3>
                      {r.category && <Badge variant="muted">{r.category}</Badge>}
                    </div>
                    {r.excerpt && <p className="mt-1 text-sm text-muted-foreground">{r.excerpt}</p>}
                    <p className="mt-2 text-[10px] text-muted-foreground">
                      Updated {formatDate(r.updated_at)} · {r.view_count} view{r.view_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-center text-[10px] text-muted-foreground">
        Article detail view will be added in P63-68 polish.
      </p>
    </div>
  )
}

// Helpful for build linter
export function PortalIndexRedirect() {
  const { token } = useParams<{ token: string }>()
  return (
    <Card>
      <CardContent className="py-6 text-center">
        <p className="text-sm">Loading portal for token <code className="font-mono">{token?.slice(0, 8)}…</code></p>
      </CardContent>
    </Card>
  )
}

// Suppress unused-import warning
void Button
